/**
 * Envelope-unwrapping REST verbs over the shared api-client.
 *
 * The settings API modules were five near-identical transcriptions of one
 * contract — fetch, unwrap a single-key envelope, return the payload — that
 * had drifted into four envelope/return shapes. `resource<T>` owns the verbs
 * once; a module now only declares its types, paths, and envelope keys.
 *
 * Omit `envelope` for endpoints whose payload is the whole response body
 * (the Xero endpoints). DELETE returns no body either way.
 */
import { apiFetch } from "../../../shared/api-client";

export interface Resource<T> {
  get: (signal?: AbortSignal) => Promise<T>;
  post: (body: unknown) => Promise<T>;
  put: (body: unknown) => Promise<T>;
  patch: (body: unknown) => Promise<T>;
  del: (query?: Record<string, string>) => Promise<void>;
}

export function resource<T>(path: string, envelope?: string): Resource<T> {
  const call = async (opts: {
    method?: string;
    body?: unknown;
    signal?: AbortSignal;
  }): Promise<T> => {
    if (envelope === undefined) return apiFetch<T>(path, opts);
    const res = await apiFetch<Record<string, T>>(path, opts);
    return res[envelope] as T;
  };
  return {
    get: (signal?: AbortSignal) => call({ signal }),
    post: (body: unknown) => call({ method: "POST", body }),
    put: (body: unknown) => call({ method: "PUT", body }),
    patch: (body: unknown) => call({ method: "PATCH", body }),
    del: async (query?: Record<string, string>) => {
      await apiFetch<void>(path, { method: "DELETE", query });
    },
  };
}
